import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Check, Clock, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  for (let i = first.getDay() - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, inMonth: false });
  }
  for (let d = 1; d <= last.getDate(); d++) {
    days.push({ date: new Date(year, month, d), inMonth: true });
  }
  while (days.length < 42) {
    const n = days.length - (first.getDay() + last.getDate()) + 1;
    days.push({ date: new Date(year, month + 1, n), inMonth: false });
  }
  return days.slice(0, 42);
};

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const STATUS_STYLES = {
  available: { bg: 'bg-[#06D6A0]/10', dot: 'bg-[#06D6A0]', text: 'text-[#06D6A0]', label: 'Available' },
  limited:   { bg: 'bg-[#FFD166]/15', dot: 'bg-[#E8A80E]', text: 'text-[#9C6B12]', label: 'Limited' },
  booked:    { bg: 'bg-red-50',        dot: 'bg-red-500',  text: 'text-red-600',    label: 'Booked' },
  past:      { bg: 'bg-transparent',   dot: 'bg-gray-300', text: 'text-gray-400',   label: 'Past' },
};

/**
 * Inner calendar — no outer framing.
 * Props:
 *  - compact: smaller padding / cells for modal use
 *  - onPicked(dateStr): called just before navigation (use to close a host modal)
 */
export const AvailabilityCalendar = ({ compact = false, onPicked }) => {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  // Step 2 state: time slot picker for the selected date
  const [pickedDate, setPickedDate] = useState(null);
  const [slots, setSlots] = useState(null); // { available_slots, booked_slots } | null
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = () => {
      if (cancelled) return;
      setLoading(true);
      axios
        .get(`${API_URL}/api/availability/month`, { params: { year, month: month + 1 } })
        .then((r) => { if (!cancelled) setDays(r.data.days || {}); })
        .catch(() => { if (!cancelled) setDays({}); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    fetch();
    // Poll every 60s so customers see a slot flip from 'available' → 'booked'
    // the moment another visitor completes a booking. Pairs with the server-
    // side SSE `calendar_changed` broadcast for authenticated dashboards.
    const interval = setInterval(fetch, 60000);
    // Also refetch whenever the browser tab regains focus (handles the
    // "I had the page open, just switched back" case instantly).
    const onVisibility = () => { if (!document.hidden) fetch(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [year, month]);

  const grid = useMemo(() => getGrid(year, month), [year, month]);
  const monthLabel = new Date(year, month).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  const go = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth());
  };

  const pickDate = async (dateStr, status) => {
    if (status === 'past' || status === 'booked') return;
    setPickedDate(dateStr);
    setSlots(null);
    setSlotsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/bookings/available-slots/${dateStr}`);
      setSlots(res.data);
    } catch {
      setSlots({ available_slots: [], booked_slots: [], error: true });
    } finally {
      setSlotsLoading(false);
    }
  };

  const pickTimeSlot = (timeSlot) => {
    if (!pickedDate) return;
    onPicked?.(pickedDate);
    navigate(`/book?date=${pickedDate}&time_slot=${timeSlot}`);
  };

  const goBackToCalendar = () => {
    setPickedDate(null);
    setSlots(null);
  };

  const cellSize = compact ? 'text-xs' : 'text-sm';

  // Step 2 view: time-slot grid for the selected date
  if (pickedDate) {
    const dateLabel = new Date(pickedDate + 'T00:00:00').toLocaleDateString('en-CA', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
    const TIME_LABELS = { '10:00': '10:00 AM', '13:00': '1:00 PM', '16:00': '4:00 PM' };
    const ALL_SLOTS = ['10:00', '13:00', '16:00'];
    const available = slots?.available_slots || [];
    return (
      <motion.div
        key="step-time"
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -12 }}
        transition={{ duration: 0.22 }}
        className="bg-white rounded-2xl border border-[#E8E5DF] p-3 sm:p-4"
        data-testid="hero-time-step"
      >
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={goBackToCalendar}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#6B6B7A] hover:text-[#1E1E24]"
            data-testid="hero-time-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to calendar
          </button>
          <Clock className="w-4 h-4 text-[#FF3B6B]" />
        </div>
        <div className="text-center mb-4">
          <p className="text-xs text-[#6B6B7A] uppercase tracking-wider font-semibold">Selected date</p>
          <p className="text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{dateLabel}</p>
        </div>
        {slotsLoading ? (
          <p className="text-center text-sm text-[#6B6B7A] py-6">Loading time slots…</p>
        ) : available.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm font-semibold text-[#FF3B6B]">No slots available</p>
            <p className="text-xs text-[#6B6B7A] mt-1">All time slots for this date are booked. Please pick another date.</p>
            <button
              onClick={goBackToCalendar}
              className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#1E1E24] text-white text-xs font-semibold hover:bg-black"
              data-testid="hero-time-back-noslot"
            >
              Choose another date
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2" data-testid="hero-time-grid">
            {ALL_SLOTS.map((slot) => {
              const isAvail = available.includes(slot);
              return (
                <button
                  key={slot}
                  type="button"
                  disabled={!isAvail}
                  onClick={() => pickTimeSlot(slot)}
                  data-testid={`hero-time-slot-${slot}`}
                  className={[
                    'p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 font-semibold',
                    isAvail
                      ? 'border-[#06D6A0]/50 bg-[#06D6A0]/5 hover:border-[#06D6A0] hover:bg-[#06D6A0]/15 hover:scale-105 cursor-pointer text-[#1E1E24]'
                      : 'border-[#F0EDE6] bg-[#F8F6F1] text-[#9a9aa6] cursor-not-allowed',
                  ].join(' ')}
                >
                  <Clock className="w-4 h-4" />
                  <span className="text-base">{TIME_LABELS[slot] || slot}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold">
                    {isAvail ? 'Available' : 'Booked'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-center text-[11px] text-[#06D6A0] inline-flex items-center justify-center gap-1 w-full">
          <Check className="w-3 h-3" /> Pick a time to continue with package selection
        </p>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
    <motion.div
      key="step-date"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-2xl border border-[#E8E5DF] p-3 sm:p-4"
    >
      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => go(-1)}
          className="p-2 rounded-full border border-[#E8E5DF] hover:bg-[#FFF6EC]"
          aria-label="Previous month"
          data-testid="avail-prev"
        ><ChevronLeft className="w-4 h-4" /></button>
        <p className="text-base font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="avail-month-label">
          {monthLabel}
        </p>
        <button
          onClick={() => go(1)}
          className="p-2 rounded-full border border-[#E8E5DF] hover:bg-[#FFF6EC]"
          aria-label="Next month"
          data-testid="avail-next"
        ><ChevronRight className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[10px] font-semibold text-[#6B6B7A] py-1">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" data-testid="avail-grid">
        {grid.map((d, i) => {
          const dateStr = iso(d.date);
          const status = !d.inMonth ? 'other' : (days[dateStr] || 'past');
          const s = STATUS_STYLES[status] || { bg: 'bg-transparent', dot: 'bg-gray-200', text: 'text-gray-400', label: '' };
          const clickable = d.inMonth && (status === 'available' || status === 'limited');
          const isToday = iso(today) === dateStr;
          return (
            <button
              key={i}
              type="button"
              disabled={!clickable}
              onClick={() => pickDate(dateStr, status)}
              aria-label={`${dateStr} — ${s.label}`}
              data-testid={d.inMonth ? `avail-day-${dateStr}` : undefined}
              className={[
                'relative aspect-square rounded-lg flex flex-col items-center justify-center transition-all',
                d.inMonth ? s.bg : 'bg-transparent',
                clickable ? 'cursor-pointer hover:scale-[1.04] hover:shadow-md border border-[#E8E5DF]' : 'cursor-default opacity-50',
                isToday && d.inMonth ? 'ring-2 ring-[#FF3B6B] ring-offset-1' : '',
              ].join(' ')}
            >
              <span className={`${cellSize} font-semibold ${d.inMonth ? s.text : 'text-gray-300'}`}>
                {d.date.getDate()}
              </span>
              {d.inMonth && status !== 'past' && status !== 'other' && (
                <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${s.dot}`} />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-3 justify-center text-[11px] text-[#6B6B7A]">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#06D6A0]" /> Available</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E8A80E]" /> Limited</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Fully booked</span>
        {loading && <span className="italic">loading…</span>}
      </div>

      <div className="mt-2 text-center text-[11px] text-[#06D6A0] inline-flex items-center justify-center gap-1 w-full">
        <Check className="w-3 h-3" /> No login required — pick a date to start your booking
      </div>
    </motion.div>
    </AnimatePresence>
  );
};

/**
 * Legacy full-section wrapper (kept for backward compatibility if referenced elsewhere).
 */
export const HeroAvailabilityCalendar = () => (
  <section
    id="availability"
    className="py-10 sm:py-14 fade-up"
    style={{ background: 'linear-gradient(180deg, #FFFDF8 0%, #FFF6EC 100%)' }}
    data-testid="hero-availability-calendar"
  >
    <div className="container mx-auto px-4 max-w-3xl">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[#E8E5DF] shadow-sm text-sm font-medium text-[#1E1E24] mb-3">
          <CalIcon className="w-4 h-4 text-[#FF3B6B]" /> Check availability
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Pick a date for your party
        </h2>
      </motion.div>
      <AvailabilityCalendar />
    </div>
  </section>
);

export default HeroAvailabilityCalendar;
